import React from "react";
import { useRoomContext } from "@livekit/components-react";
import { PUPPET, CHAR_NAME } from "@/lib/tokens";
import { createAudioAnalyser } from "livekit-client";
import { observeReceivedMouth } from "@/lib/receivedMouth";

// Existing owned mouth frames, driven by received audio amplitude, not phonemes.
export default function AiParticipant({ characterIds = ["bruce"] }) {
  const room = useRoomContext();
  const [state, setState] = React.useState({ ready: false, speaking: false, audible: false, character: null });
  const [mouthState, setMouth] = React.useState({ track: null, shape: 'neutral' });
  const mouth = mouthState.track === state.track ? mouthState.shape : 'neutral';
  const [analysisUnavailable, setAnalysisUnavailable] = React.useState(false);
  const castKey = characterIds.join(',');
  React.useEffect(() => {
    if (!room) return;
    const update = () => {
      const ai = room.remoteParticipants.get("tt-ai-rehearsal");
      const ready = room.state === "connected" && !!ai &&
        [...(ai.audioTrackPublications?.values() || [])].some(p => p.isSubscribed && p.track && !p.isMuted);
      const active = [...(ai?.audioTrackPublications?.values() || [])].filter(p => p.isSubscribed && p.track && !p.isMuted);
      const named = active.find(p => p.trackName?.startsWith("tt-character:"));
      // Legacy single-Bruce track remains compatible; unknown/multiple active named tracks never animate another character.
      const character = active.length === 1 ? (named ? named.trackName.slice(13) : "bruce") : null;
      const connected = room.state === "connected" && !!ai && [...(ai.audioTrackPublications?.values() || [])].some(p => p.isSubscribed && p.track);
      const selected = castKey.split(',').includes(character) && PUPPET[character];
      setState({ ready: connected, speaking: ready && !!ai.isSpeaking, audible: !!room.canPlaybackAudio,
        character, track: ready && selected && room.canPlaybackAudio ? active[0]?.track : null });
    };
    const events = ["activeSpeakersChanged", "participantConnected", "participantDisconnected",
      "trackSubscribed", "trackUnsubscribed", "trackUnpublished", "trackMuted", "trackUnmuted", "connectionStateChanged", "disconnected", "audioPlaybackChanged"];
    update(); events.forEach(e => room.on(e, update));
    return () => events.forEach(e => room.off(e, update));
  }, [room, castKey]);
  React.useEffect(() => {
    setMouth({ track: state.track, shape: 'neutral' }); setAnalysisUnavailable(false);
    if (!state.track) return;
    return observeReceivedMouth(state.track, createAudioAnalyser,
      shape => setMouth({ track: state.track, shape }), () => setAnalysisUnavailable(true));
  }, [state.track, state.character]);
  const ids = characterIds.filter(id => PUPPET[id] && CHAR_NAME[id]);
  return <div>{ids.map(id => <section key={id} aria-label={`${CHAR_NAME[id]} AI co-host`} className="flex items-center gap-4 p-3 border-t border-white/20 text-white">
    <div data-testid={`received-mouth-${id}`} data-mouth={state.track && state.character === id ? mouth : 'neutral'}
      style={{ position: 'relative', width: 112, height: Math.round(112 / PUPPET[id].aspect), flexShrink: 0,
        border: `3px solid ${state.speaking && state.character === id ? "#7ec4e8" : "transparent"}`, borderRadius: 12 }}>
      <img src={PUPPET[id].base} alt={`${CHAR_NAME[id]}, Turf Talk AI co-host`}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      {state.track && state.character === id && mouth !== 'neutral' && <img
        src={PUPPET[id].mouth[mouth]} alt="" aria-hidden="true" data-testid={`mouth-overlay-${id}`}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />}
    </div>
    <div><h2>{CHAR_NAME[id]} · AI co-host</h2>
      <p role="status">{!state.ready ? "AI audio is not connected." : state.speaking && state.character === id ? "AI speaking" : "AI connected · not speaking"}</p>
      {state.ready && !state.audible && <p>Enable room sound to hear the AI.</p>}
      <p className="text-xs text-white/60">Mouth movement follows received AI audio amplitude, not phoneme-accurate lip sync. No audio is recorded.</p>
      {analysisUnavailable && state.character === id && <p>Mouth animation is unavailable in this browser; room audio is unchanged.</p>}
    </div>
  </section>)}</div>;
}
